export type AnalyticsEnvironment =
  | "production"
  | "preview"
  | "development"
  | "unknown";

export type AnalyticsEnvironmentContext = {
  analytics_environment: AnalyticsEnvironment;
  is_test_account: boolean;
  analytics_traffic_class: "production_human" | "preview" | "test";
};

const PRODUCTION_HOSTS = new Set(["fairpath.app", "www.fairpath.app"]);
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "::1"]);

export function buildAnalyticsEnvironmentContext({
  hostname,
  isDevelopmentBuild = false,
}: {
  hostname?: string | null;
  isDevelopmentBuild?: boolean;
}): AnalyticsEnvironmentContext {
  const normalizedHostname = hostname?.trim().toLowerCase() ?? "";

  if (isDevelopmentBuild || LOCAL_HOSTS.has(normalizedHostname)) {
    return {
      analytics_environment: "development",
      is_test_account: true,
      analytics_traffic_class: "test",
    };
  }

  if (PRODUCTION_HOSTS.has(normalizedHostname)) {
    return {
      analytics_environment: "production",
      is_test_account: false,
      analytics_traffic_class: "production_human",
    };
  }

  if (normalizedHostname.endsWith(".vercel.app")) {
    return {
      analytics_environment: "preview",
      is_test_account: true,
      analytics_traffic_class: "preview",
    };
  }

  // Unknown hosts fail closed so development, automated, and unexpected
  // environments cannot silently enter production-human reporting.
  return {
    analytics_environment: "unknown",
    is_test_account: true,
    analytics_traffic_class: "test",
  };
}

export function getBrowserAnalyticsEnvironmentContext(): AnalyticsEnvironmentContext {
  const context = buildAnalyticsEnvironmentContext({
    hostname: typeof window === "undefined" ? null : window.location.hostname,
    isDevelopmentBuild: import.meta.env.MODE === "development",
  });
  if (context.analytics_environment === "production" && getAnalyticsAudience() !== "eligible_unclassified") {
    return { ...context, is_test_account: true, analytics_traffic_class: "test" };
  }
  return context;
}

export function isBrowserAnalyticsEnvironmentAllowed(): boolean {
  return buildAnalyticsEnvironmentContext({
    hostname: typeof window === "undefined" ? null : window.location.hostname,
    isDevelopmentBuild: import.meta.env.MODE === "development",
  }).analytics_environment === "production";
}

/**
 * Browser analytics are collected only on FairPath's canonical production
 * hosts. Preview, local, and unknown hosts remain observable through their
 * own developer tooling without contaminating customer-demand metrics.
 */
export function isBrowserAnalyticsCaptureAllowed(): boolean {
  const context = getBrowserAnalyticsEnvironmentContext();
  return context.analytics_environment === "production" &&
    context.analytics_traffic_class === "production_human" &&
    !context.is_test_account && hasAcceptedAnalyticsConsent();
}

export function withAnalyticsEnvironmentContext(
  properties?: Record<string, unknown>,
): Record<string, unknown> {
  return {
    ...(properties ?? {}),
    ...getBrowserAnalyticsEnvironmentContext(),
    analytics_audience: getAnalyticsAudience(),
  };
}

/** Serialized context accepted by the property-lookup server boundary. */
export function getAnalyticsRequestContext(): {
  analyticsEnvironment: AnalyticsEnvironment;
  analyticsIsTestAccount: boolean;
  analyticsTrafficClass: AnalyticsEnvironmentContext["analytics_traffic_class"];
} {
  const context = getBrowserAnalyticsEnvironmentContext();
  return {
    analyticsEnvironment: context.analytics_environment,
    analyticsIsTestAccount: context.is_test_account,
    analyticsTrafficClass: context.analytics_traffic_class,
  };
}
import { getAnalyticsAudience, hasAcceptedAnalyticsConsent } from "./analyticsAudience";
